import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Divider, IconButton, InputBase, Modal, Stack, styled, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useLocation, useNavigate, useParams } from 'react-router';
import { LoadingButton } from '@mui/lab';
import Iconify from '../../../../components/Iconify';
import conObj from '../../../../utils/connection-assistant';
import useLocales from '../../../../hooks/useLocales';
import { useDispatch } from '../../../../redux/store';
import { openLoginSignup } from '../../../../redux/slices/authPages';
import CustomModal from '../../../../components/CustomModal';
import { InputBorder, InputContainer, StyledInput, ThemeButton } from '../../../../components/settings/GlobalStyled';
import palette from '../../../../theme/palette';

const FormContainer = styled(Box)(({ theme }) => ({
  padding: '16px',
  borderRadius: '4px',
  maxWidth: '900px',
  marginRight: 'auto',
  marginLeft: 'auto',
  marginTop: '10px',
  background: 'none',
}));

const snackbarOptionsSuccess = { variant: 'success', autoHideDuration: 5000 };
const snackbarOptionsError = { variant: 'error', autoHideDuration: 5000 };

const ResetPasswordDialog = () => {
  const { enqueueSnackbar } = useSnackbar();

  const { translate } = useLocales();

  const location = useLocation();

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    newPassword: {
      value: '',
      show: false,
    },
    confirmPassword: {
      value: '',
      show: false,
    },
    otpCode: '',
  });

  const [openResetPasswordDialog, setOpenResetPasswordDialog] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (location?.pathname === '/auth/reset-password' && !location?.state?.otpUuid && !location?.state?.phoneNumber) {
      navigate('/');
      return;
    }

    if (location?.state?.otpUuid) setOpenResetPasswordDialog(true);
  }, [location?.pathname, location?.state]);

  const handleFormChange = (e, showName = null) => {
    if (showName) setFormData((prev) => ({ ...prev, [showName]: { show: !prev[showName]?.show } }));
    else
      setFormData((prev) => ({
        ...prev,
        [e.target?.name]: e.target?.name === 'otpCode' ? e.target?.value : { value: e.target?.value },
      }));
  };

  const onSubmit = (e) => {
    e?.preventDefault();

    if (!formData?.otpCode) {
      enqueueSnackbar(translate('x_is_required', { x: translate('verification_code') }), snackbarOptionsError);
      return;
    }

    if (formData?.newPassword?.value.trim().length < 6) {
      enqueueSnackbar(translate('password_atleast_6_characters'), snackbarOptionsError);
      return;
    }

    if (formData?.confirmPassword?.value !== formData?.newPassword?.value) {
      enqueueSnackbar(translate('password_doesnt_match'), snackbarOptionsError);
      return;
    }

    setIsSubmitting(true);

    conObj
      .post(`password_reset_otp.json`, {
        'user-new_password': formData?.newPassword?.value?.trim(),
        'user-no_hp': location?.state?.phoneNumber,
        otp_uuid: location?.state?.otpUuid,
        otp_code: formData?.otpCode,
      })
      .then((res) => {
        if (res.status === 200) {
          const { msg, status } = res.data?.data;
          if (status === 1) {
            enqueueSnackbar(translate('password_reset_success'), snackbarOptionsSuccess);
            navigate('/');
            // setOpenLoginSignup({ open: true, isLogin: true });
          }
        }
      })
      .catch((err) => {
        const snackbarOptions = { variant: 'error', autoHideDuration: 5000 };
        enqueueSnackbar(err.message, snackbarOptions);
        console.log(`Server Error: ${err.message}`);
      })
      .finally(() => setIsSubmitting(false));
  };

  const onCloseDialog = () => {
    setOpenResetPasswordDialog(false);
    navigate('/');
  };

  return (
    <CustomModal open={openResetPasswordDialog} onClose={onCloseDialog} sx={{ overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        <Typography sx={{ fontWeight: 600, color: '#000', fontSize: '18px', mb: 2 }}>
          {translate('reset_password')}
        </Typography>

        <FormContainer component={'form'} onSubmit={onSubmit}>
          <Typography sx={{ fontWeight: 300, color: '#000', fontSize: '13px', mb: 2 }}>
            {translate('otp_sent_enter_code_password')}
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Box sx={{ mb: 2 }}>
                <InputBorder>
                  <InputContainer direction={'row'}>
                    <Iconify icon={'material-symbols:key'} width={24} height={24} sx={{ color: '#55657e' }} />
                    <StyledInput
                      type="number"
                      name="otpCode"
                      value={formData?.otpCode}
                      placeholder={translate('verification_code')}
                      onChange={handleFormChange}
                    />
                  </InputContainer>
                </InputBorder>
              </Box>
              <Box sx={{ mb: 2 }}>
                <InputBorder>
                  <InputContainer direction={'row'}>
                    <Iconify icon={'ph:lock-light'} width={24} height={24} sx={{ color: '#55657e' }} />
                    <StyledInput
                      type={formData?.newPassword?.show ? 'text' : 'password'}
                      name="newPassword"
                      value={formData?.newPassword?.value}
                      placeholder={translate('new_password')}
                      onChange={handleFormChange}
                    />
                    <IconButton size="small" onClick={(e) => handleFormChange(e, 'newPassword')}>
                      <Iconify
                        icon={formData?.newPassword?.show ? 'ph:eye-light' : 'ph:eye-closed-light'}
                        sx={{ cursor: 'pointer' }}
                      />
                    </IconButton>
                  </InputContainer>
                </InputBorder>
              </Box>
              <Box sx={{ mb: 2 }}>
                <InputBorder>
                  <InputContainer direction={'row'}>
                    <Iconify icon={'ph:lock-light'} width={24} height={24} sx={{ color: '#55657e' }} />
                    <StyledInput
                      type={formData?.confirmPassword?.show ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData?.confirmPassword?.value}
                      placeholder={translate('confirm_password')}
                      onChange={handleFormChange}
                    />
                    <IconButton size="small" onClick={(e) => handleFormChange(e, 'confirmPassword')}>
                      <Iconify
                        icon={formData?.confirmPassword?.show ? 'ph:eye-light' : 'ph:eye-closed-light'}
                        sx={{ cursor: 'pointer' }}
                      />
                    </IconButton>
                  </InputContainer>
                </InputBorder>
              </Box>

              <ThemeButton variant="contained" type="submit" fullWidth loading={isSubmitting}>
                {translate('confirm')}
              </ThemeButton>
            </Box>
          </Stack>
        </FormContainer>
      </Box>
    </CustomModal>
  );
};

ResetPasswordDialog.propTypes = {};

export default ResetPasswordDialog;
